import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyGetProfileInfoQuery } from '../../Account/api';
import { useLogout } from '../../Account/hooks/useLogout';
import { setProfile } from '../../Account/state/profileSlice';
import { Loading } from '../../components/Loading';
import {
	setBirthdaySet,
	setOAuthTokens,
	setUserId
} from '../../domains/Auth/slice';
import { showBannerPrompt } from '../../domains/UI/store/slice';
import { ErrorMessage } from '../../GlobalComponents/ErrorMessage';
import { useAppDispatch } from '../../reduxstore/hooks';
import { setUserSession } from '../../utils/userStorage';
import { useOAuthLoginMutation } from '../api/oauth';
import { create_pkce_verifier, pkce_challenge_from_verifier } from './pkce';
import {
	setLocalAccessToken,
	setLocalRefreshToken,
	setLocalUserId
} from '../../MedianFunctions';

const OAuth = (): JSX.Element => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const queryParameters = new URLSearchParams(window.location.search);
	const code = queryParameters.get('code');
	const redirectSource = queryParameters.get('redirect');
	const [isReachable, setReachable] = useState(true);
	const [isXHRLoading, setXHRLoading] = useState(false);

	const logout = useLogout();

	const [oAuthLogin, { isError, isLoading }] = useOAuthLoginMutation();
	const [getProfileInfo] = useLazyGetProfileInfoQuery();

	const resetSessionStorage = (): boolean => {
		try {
			sessionStorage.clear();
			return true;
		} catch (error) {
			return false;
		}
	};

	const getCodeVerifier = (): string | null => {
		try {
			const codeVerifier = sessionStorage.getItem('codeVerifier');
			return codeVerifier;
		} catch (error) {
			// console.debug(error);
			return null;
		}
	};

	// All functions are scoped in useEffect for compatibility
	useEffect(() => {
		const client_id = import.meta.env.VITE_APP_OAUTH_CLIENT_ID;
		const grant_type = import.meta.env.VITE_APP_GRANT_TYPE;
		const redirect_uri = `${window.location.origin}/login`;

		const createNewPKCE = async () => {
			const isSessionStorageReset = resetSessionStorage();

			/// execution start
			if (redirectSource) {
				sessionStorage.setItem('redirectSource', `${redirectSource}`);
			}

			if (isSessionStorageReset) {
				const newCodeVerifier = create_pkce_verifier();
				const newCodeChallenge = await pkce_challenge_from_verifier(
					newCodeVerifier
				);

				sessionStorage.setItem('codeVerifier', newCodeVerifier);
				sessionStorage.setItem('codeChallenge', newCodeChallenge);

				const apiUrl = `${
					import.meta.env.VITE_APP_OAUTH_API_URL
				}/o/authorize/`;

				const searchParams = new URLSearchParams({
					response_type: 'code',
					code_challenge: newCodeChallenge,
					code_challenge_method: 'S256',
					redirect_uri: redirect_uri,
					client_id: `${import.meta.env.VITE_APP_OAUTH_CLIENT_ID}`
				});

				const exportUrl = `${apiUrl}?${searchParams.toString()}`;
				const xhr = new XMLHttpRequest();

				setXHRLoading(true);
				xhr.onreadystatechange = () => {
					if (xhr.readyState === 4 && xhr.status === 0) {
						setReachable(false);
						setXHRLoading(false);
					} else if (xhr.readyState === 4 && xhr.status === 200) {
						setReachable(true);
						window.location.href = exportUrl;
					}
				};

				xhr.open('head', exportUrl);
				xhr.send(null);

				return;
			} else {
				return;
			}
		};

		const handleOAuthLogin = () => {
			const existingCodeVerifier = getCodeVerifier();
			const redirectSource = sessionStorage.getItem('redirectSource');

			if (code && existingCodeVerifier) {
				const payload = {
					code_verifier: existingCodeVerifier,
					grant_type,
					redirect_uri,
					code,
					client_id
				} as IOAuthPayload;

				const encodedPayload = new URLSearchParams({
					...payload
				}) as unknown as string;
				const login = (payload: string) => {
					oAuthLogin(payload)
						.unwrap()
						.then(res => {
							// localStorage
							setUserSession({
								accessToken: res.access_token,
								refreshToken: res.refresh_token
							} as AuthState);

							// redux
							dispatch(
								setOAuthTokens({
									accessToken: res.access_token,
									refreshToken: res.refresh_token
								} as OAuthPayload)
							);

							// Median -- IOS

							if (navigator.userAgent.indexOf('median') > -1) {
								setLocalAccessToken(res.access_token);
								setLocalRefreshToken(res.refresh_token);
							}

							// TODO: Median -- Android
							//
							//
							//

							getProfileInfo(null)
								.unwrap()
								.then((res: ProfileResponse) => {
									dispatch(setProfile(res.data.profile));
									dispatch(setUserId(res.data.id));

									if (res.data.profile.bad_email) {
										dispatch(showBannerPrompt());
									}

									if (
										navigator.userAgent.indexOf('median') >
										-1
									) {
										setLocalUserId(String(res.data.id));
									}

									dispatch(
										setBirthdaySet(res.data.birthday_set)
									);
								})
								.then(() => {
									// Reset the redirect configuration after a successful redirection.
									sessionStorage.removeItem('redirectSource');
									navigate(
										redirectSource
											? redirectSource
											: '/account/rewards'
									);
								});
						})
						.catch(() => logout());
				};

				login(encodedPayload);
			}
		};

		if (code) {
			return handleOAuthLogin();
		}

		createNewPKCE();
	}, [
		code,
		redirectSource,
		oAuthLogin,
		dispatch,
		navigate,
		getProfileInfo,
		logout
	]);

	return (
		<div>
			<span
				style={{
					display: 'block',
					margin: '3rem auto',
					width: '100%',
					textAlign: 'center',
					fontSize: '20px',
					color: '#575757',
					fontWeight: 600
				}}
			>
				<p> {isLoading && 'Signing you in...'} </p>
				{isLoading || (isXHRLoading && <Loading />)}
			</span>

			{(isError || !isReachable) && (
				<ErrorMessage
					message={
						'It looks like we were unable to reach our servers. Please try again later. You can still use the app in guest mode.'
					}
				/>
			)}
		</div>
	);
};

export default OAuth;

interface IOAuthPayload {
	[key: string]: string;
}
